export type MediaType = 'youtube' | 'instagram' | 'tiktok' | 'video' | 'image' | 'none';

export interface ParsedMedia {
  type: MediaType;
  id?: string;
  embedUrl?: string;
  directUrl?: string;
}

/**
 * Parses a URL (platform link or direct media) into a structured ParsedMedia object.
 * Uploaded/storage URLs (mediaUrl) are always checked first.
 */
export function parseMediaUrl(url?: string, mediaUrl?: string): ParsedMedia {
  // 1. Uploaded media (MinIO / S3 / blob) — highest priority
  if (mediaUrl?.trim()) {
    const target = mediaUrl.trim();
    const imageExts = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
    if (imageExts.test(target)) return { type: 'image', directUrl: target };
    return { type: 'video', directUrl: target };
  }

  // 2. Platform URL
  const target = url?.trim();
  if (!target) return { type: 'none' };

  // YouTube
  const ytPattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/;
  const ytMatch = target.match(ytPattern);
  if (ytMatch) {
    return {
      type: 'youtube',
      id: ytMatch[1],
      embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`,
    };
  }

  // Instagram
  const igPattern = /instagram\.com\/(?:p|reel|reels)\/([a-zA-Z0-9_-]+)/;
  const igMatch = target.match(igPattern);
  if (igMatch) {
    return {
      type: 'instagram',
      id: igMatch[1],
      embedUrl: `https://www.instagram.com/p/${igMatch[1]}/embed`,
    };
  }

  // TikTok
  const tiktokPatterns = [
    /tiktok\.com\/@[^/]+\/video\/(\d+)/,
    /tiktok\.com\/t\/([a-zA-Z0-9]+)/,
    /vm\.tiktok\.com\/([a-zA-Z0-9]+)/,
    /vt\.tiktok\.com\/([a-zA-Z0-9]+)/,
  ];
  for (const pattern of tiktokPatterns) {
    const match = target.match(pattern);
    if (match) return { type: 'tiktok', id: match[1], directUrl: target };
  }

  // Direct video file
  const videoExts = /\.(mp4|webm|mov|m4v|avi|mkv)(\?.*)?$/i;
  if (videoExts.test(target)) return { type: 'video', directUrl: target };

  // Direct image file
  const imageExts = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
  if (imageExts.test(target)) return { type: 'image', directUrl: target };

  return { type: 'none' };
}
